import React, { useId } from 'react';
import type { BillingCadence } from '../../config/commercialPlans';
import { cn } from '../../lib/utils';

export function PricingBillingToggle({
  billingCadence,
  onSelect,
  title,
  monthlyLabel,
  annualLabel,
  annualBadge,
  className,
}: {
  billingCadence: BillingCadence;
  onSelect: (cadence: BillingCadence) => void;
  title: string;
  monthlyLabel: string;
  annualLabel: string;
  annualBadge?: string;
  className?: string;
}): React.JSX.Element {
  const titleId = useId();

  return (
    <div className={className}>
      <div className="mb-4 flex items-center justify-between gap-4">
        <p
          id={titleId}
          className="text-xs font-bold uppercase tracking-[0.14em] text-[var(--ir-muted)]"
        >
          {title}
        </p>
        {billingCadence === 'annual' && annualBadge ? (
          <span className="rounded-full bg-brand-accent px-3 py-1 text-xs font-bold uppercase tracking-[0.08em] text-white">
            {annualBadge}
          </span>
        ) : null}
      </div>
      <div
        role="group"
        aria-labelledby={titleId}
        className="grid grid-cols-2 gap-1 rounded-full border border-brand-primary/10 bg-brand-bg p-1"
      >
        {(['monthly', 'annual'] as const).map((cadence) => (
          <button
            key={cadence}
            type="button"
            aria-pressed={billingCadence === cadence}
            onClick={() => onSelect(cadence)}
            className={cn(
              'min-h-11 rounded-full px-5 py-2.5 text-xs font-bold tracking-[0.04em] transition-colors',
              billingCadence === cadence
                ? 'bg-brand-primary text-white shadow-sm'
                : 'text-[var(--ir-muted)] hover:bg-white'
            )}
          >
            {cadence === 'annual' ? annualLabel : monthlyLabel}
          </button>
        ))}
      </div>
    </div>
  );
}
